/**
 * AuthContext — wraps the imperative TokenProvider in a React-friendly
 * surface. Exposes the current `Me` (decoded access token claims), a
 * `login()` action, and a `logout()` action. Pages call `useAuth()` to
 * read the user; the protected layout calls `useAuth()` to guard rendering.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { LoginRequest, Me, TokenPair } from "@dfp/api-client";
import { api } from "../lib/api.js";
import { browserTokens } from "../lib/tokens.js";

interface AuthState {
  readonly status: "loading" | "authenticated" | "anonymous";
  readonly user: Me | null;
}

interface AuthContextValue extends AuthState {
  login(input: LoginRequest): Promise<{ mustChangePassword: boolean }>;
  logout(): void;
  reloadMe(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ status: "loading", user: null });

  const reloadMe = useCallback(async () => {
    if (!browserTokens.getAccessToken()) {
      setState({ status: "anonymous", user: null });
      return;
    }
    try {
      const me = await api.get<Me>("/api/auth/me");
      setState({ status: "authenticated", user: me });
    } catch {
      // ApiClient will have already attempted refresh + cleared tokens on
      // 401; we just reflect the outcome.
      setState({ status: "anonymous", user: null });
    }
  }, []);

  useEffect(() => {
    void reloadMe();
  }, [reloadMe]);

  const login = useCallback(
    async (input: LoginRequest): Promise<{ mustChangePassword: boolean }> => {
      const tokens = await api.post<TokenPair & { mustChangePassword?: true }>(
        "/api/auth/login",
        input,
        { public: true },
      );
      browserTokens.saveTokens(tokens);
      await reloadMe();
      // Surface the force-change flag so LoginPage can route to
      // /auth/change-password instead of /. We return rather than
      // navigating from here so the route is a property of the
      // surface, not buried in the auth provider.
      return { mustChangePassword: tokens.mustChangePassword === true };
    },
    [reloadMe],
  );

  const logout = useCallback((): void => {
    browserTokens.clearTokens();
    setState({ status: "anonymous", user: null });
    window.location.assign("/login");
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, reloadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() must be used within <AuthProvider>");
  return ctx;
}
